import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { subscriptionService, SubscriptionInfo } from '../../services/subscription.service';
import { trackSubscribeClick } from '../../services/subscription-tracking.service';
import { useUsage } from '../../hooks/useUsage';
import logo from '../../assets/gymea-flower-only.png';
import './SubscriptionStatusCard.css';

const PLAN_TITLES: Record<string, string> = {
  pro: 'Pro Plan',
  standard: 'Standard Plan',
  trial: 'Free Trial',
  free: 'Free Plan',
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  pro: 'Increased usage, for teachers planning regularly. No lock-in.',
  standard: 'Get monthly access with no lock-in commitment.',
  trial: 'Try out all our features',
  free: 'A few documents each month — upgrade any time',
};

export const SubscriptionStatusCard: React.FC = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { refresh: refreshUsage } = useUsage();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const hasHandledUrlParamRef = useRef(false);

  // A short status label only - the plan it belongs to is named alongside it, so
  // repeating "Free Trial Active" under a "Free Trial" heading buys nothing.
  const getStatusDisplay = () => {
    if (!subscriptionInfo) return null;

    // Check if subscription guards are disabled
    const useSub = process.env.REACT_APP_USE_SUB?.toLowerCase()?.trim();
    if (useSub === 'false') {
      return { type: 'disabled', label: 'Checks disabled', variant: 'info' };
    }

    const { isTrialActive, trialDays, isActive } = subscriptionInfo;

    if (isTrialActive) {
      return {
        type: 'trial',
        label: `${trialDays} day${trialDays !== 1 ? 's' : ''} remaining`,
        variant: trialDays <= 3 ? 'warning' : 'info'
      };
    }

    if (isActive) {
      return { type: 'active', label: 'Active', variant: 'success' };
    }

    // Everything else is the free plan — an ended trial, a cancelled plan, a
    // claim that says nothing useful. It is indefinite, and the account still
    // works, so it is neither "active" (nothing is being paid for) nor "not
    // active" (which read as an error state to a user who has no problem).
    return { type: 'free', label: 'Active', variant: 'info' };
  };

  const handleSyncSubscription = useCallback(async () => {
    if (!user) return;

    setSyncing(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      await subscriptionService.syncSubscriptionStatus(token);

      // Refresh usage to get updated tier and limits
      refreshUsage();

      // Show success message
      setStatusMessage("Subscription status synced successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => {
        setStatusMessage(null);
      }, 3000);

    } catch (err) {
      setError('Failed to sync subscription status');
      console.error('Error syncing subscription:', err);
    } finally {
      setSyncing(false);
    }
  }, [user, getAccessTokenSilently, refreshUsage]);

  const handleSyncAfterPayment = useCallback(async () => {
    if (!user) return;

    setSyncing(true);
    setError(null);
    setStatusMessage("Thanks for subscribing! Updating your account...");

    try {
      const token = await getAccessTokenSilently();
      await subscriptionService.syncSubscriptionStatus(token);

      // Clear URL parameter
      window.history.replaceState({}, '', window.location.pathname);

      // Refresh usage to get updated tier and limits
      refreshUsage();

      // Show success message
      setStatusMessage("Subscription activated successfully!");

      // Clear success message after 5 seconds
      setTimeout(() => {
        setStatusMessage(null);
      }, 5000);

    } catch (err) {
      setError('Failed to sync subscription status');
      setStatusMessage(null);
      console.error('Error syncing subscription:', err);
    } finally {
      setSyncing(false);
    }
  }, [user, getAccessTokenSilently, refreshUsage]);

  useEffect(() => {
    if (user) {
      const info = subscriptionService.getSubscriptionInfo(user);
      setSubscriptionInfo(info);

      // Only handle URL parameters once to prevent infinite loops
      // (getAccessTokenSilently with cacheMode: 'off' can trigger user object updates)
      if (!hasHandledUrlParamRef.current) {
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status');

        if (status === 'success' || status === 'failed') {
          hasHandledUrlParamRef.current = true;

          if (status === 'success') {
            handleSyncAfterPayment();
          } else {
            setStatusMessage("Subscription was cancelled, please try again");
            // Clear URL parameter after showing message
            window.history.replaceState({}, '', window.location.pathname);
          }
        } else {
          // Auto-sync if subscription data is stale (>6 hours old)
          if (subscriptionService.shouldSyncSubscription(user)) {
            handleSyncSubscription();
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleManageSubscription = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessTokenSilently();
      const portalUrl = await subscriptionService.getCustomerPortalUrl(token);
      
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        setError('Unable to access subscription management');
      }
    } catch (err) {
      setError('Failed to access subscription management');
      console.error('Error getting customer portal URL:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier: 'standard' | 'pro' = 'standard') => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      // Track the subscribe button click
      await trackSubscribeClick(token);

      const checkoutUrl = await subscriptionService.getSubscriptionCheckoutUrl(token, tier);

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setError('Unable to access subscription checkout');
      }
    } catch (err) {
      setError('Failed to access subscription checkout');
      console.error('Error getting checkout URL:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = getStatusDisplay();

  if (!statusInfo || !subscriptionInfo) {
    return null;
  }

  const { isTrialActive, trialDays, effectiveTier } = subscriptionInfo;

  // Resolve the plan from the same Auth0 claim the status badge above resolves
  // from. Reading it off `currentTier` (which comes from /api/usage) let the two
  // disagree: usage is null while it loads, and stays null if that request
  // fails, so a Pro subscriber saw "Active" next to "Free Trial Plan" — briefly
  // on every visit, permanently whenever /api/usage was down.
  //
  // Only the *sign-up fortnight* is named "Free Trial". A promotional grant is
  // trialing too, and calling it a trial hid the Pro entitlement its holder
  // actually has and invited them to buy the plan they were already on. An
  // ended trial is not a trial either — nothing rewrites the claim when it
  // lapses, so this showed one indefinitely.
  const resolvedTier = isTrialActive && effectiveTier === 'standard' ? 'trial' : effectiveTier;
  const planTitle = PLAN_TITLES[resolvedTier];
  const planDescription = PLAN_DESCRIPTIONS[resolvedTier];

  // The plan picker greets two different people. Nobody is at risk of losing
  // access any more — the free plan is where every ended subscription lands —
  // so the only distinction left is whether a fuller window is still running.
  const planPickerIntro = (): string => {
    if (isTrialActive) return 'Choose a plan to keep your full access after your trial ends.';
    return 'Upgrade any time for more each month.';
  };

  // Rendered inside the profile page's "Subscription plan" card, so this is a
  // plain panel rather than a card of its own with a competing heading.
  return (
    <div className="subscription-panel">
      {/* Current plan and its status, in one block */}
      <div className="subscription-plan">
        <div className="subscription-plan__icon">
          <img src={logo} alt="" />
        </div>
        <div className="subscription-plan__content">
          <h3 className="subscription-plan__title">{planTitle}</h3>
          {planDescription && (
            <p className="subscription-plan__description">{planDescription}</p>
          )}
        </div>
        <span className={`subscription-plan__status subscription-plan__status--${statusInfo.variant}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* Trial Warning */}
      {isTrialActive && trialDays <= 7 && (
        <div className="subscription-trial-warning">
          <div className="subscription-trial-warning-content">
            <p>
              Your trial ends in {trialDays} day{trialDays !== 1 ? 's' : ''} — you'll
              move to the Free plan.
            </p>
          </div>
        </div>
      )}

      {/* Status Message */}
      {statusMessage && (
        <div className="subscription-status subscription-status--info">
          <div className="subscription-status-icon">ℹ️</div>
          <div className="subscription-status-content">
            <p className="subscription-status-message">{statusMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="subscription-error">
          <p>{error}</p>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="subscription-actions">
        {statusInfo.type !== 'disabled' && (
          <>
            {subscriptionInfo.isActive ? (
              <div className="subscription-button-group">
                <button
                  className="profile-button profile-button--primary"
                  onClick={handleManageSubscription}
                  disabled={loading || syncing}
                >
                  {loading ? "Loading..." : "Manage subscription"}
                </button>

                {/* Show Upgrade to Pro button for Standard users. Read from the
                    claim, like the plan name above: /api/usage answers later
                    and sometimes not at all, and the button vanishing on a
                    failed usage request is the same bug in a different place. */}
                {effectiveTier === 'standard' && (
                  <button
                    className="profile-button profile-button--primary"
                    onClick={handleManageSubscription}
                    disabled={loading || syncing}
                  >
                    {loading ? "Loading..." : "Upgrade to Pro"}
                  </button>
                )}
              </div>
            ) : (
              // Show tier selection for trial users and inactive users
              <>
                <div className="tier-selection">
                  <p className="tier-selection-intro">{planPickerIntro()}</p>
                  <div className="tier-options">
                    <button
                      className="tier-option tier-option--standard"
                      onClick={() => handleSubscribe('standard')}
                      disabled={loading || syncing}
                    >
                      <div className="tier-option-header">
                        <h5>Standard</h5>
                        <p className="tier-price">$15/month</p>
                      </div>
                      <p className="tier-description">Get monthly access with no lock-in commitment.</p>
                    </button>
                    <button
                      className="tier-option tier-option--pro"
                      onClick={() => handleSubscribe('pro')}
                      disabled={loading || syncing}
                    >
                      <div className="tier-option-header">
                        <h5>Pro</h5>
                        <p className="tier-price">$25/month</p>
                      </div>
                      <p className="tier-description">Increased usage, for teachers planning regularly. No lock-in.</p>
                      <span className="tier-badge">Best Value</span>
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Sync button - only show when subscriptions are enabled */}
            {user && (
              <button
                className="profile-button profile-button--secondary"
                onClick={handleSyncSubscription}
                disabled={syncing || loading}
              >
                {syncing ? "Syncing..." : "Refresh status"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
